import { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import * as orgService from '../services/organization'
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus'
import { getDemoCarePlans, isDemoOrganization } from '../services/demoData'
import { hasReachedDemoLimit, saveDemoEntry, initDemoSession, getDemoEntries } from '../services/demoStorage'
import DemoUpgradePrompt from '../components/DemoUpgradePrompt'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Textarea from '../components/Textarea'
import Select from '../components/Select'
import SearchBar from '../components/SearchBar'
import BulkActions from '../components/BulkActions'
import Pagination from '../components/Pagination'
import { useNavigate } from 'react-router-dom'
import { Heart, Plus, Calendar, FileText, Pencil, X, Trash2, Download, Eye, Sparkles } from 'lucide-react'
import { formatDate } from '../utils/helpers'
import { exportCarePlansToPDF } from '../utils/pdfExport'
import { SkeletonContainer, SkeletonListItem } from '../components/SkeletonLoader'
import { useFormPersistence } from '../hooks/useFormPersistence'
import CarePlanTemplateSelector from '../components/CarePlanTemplateSelector'
import CarePlanSuggestions from '../components/CarePlanSuggestions'
import TaggedHabitsView from '../components/TaggedHabitsView'

const ITEMS_PER_PAGE = 12

const planSchema = z.object({
  clientId: z.string().uuid().optional().nullable(),
  title: z.string().min(1, 'Title is required'),
  summary: z.string().min(10, 'Summary should be at least 10 characters'),
  goals: z.string().optional(),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().optional(),
  status: z.enum(['active', 'paused', 'completed']),
  assignedUserId: z.string().uuid().optional().nullable(),
})

const statusOptions = (t) => [
  { label: t('carePlans.statusOptions.active'), value: 'active' },
  { label: t('carePlans.statusOptions.paused'), value: 'paused' },
  { label: t('carePlans.statusOptions.completed'), value: 'completed' },
]

const CarePlans = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [plans, setPlans] = useState([])
  const [clients, setClients] = useState([])
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [editingPlan, setEditingPlan] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selected, setSelected] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [reachedDemoLimit, setReachedDemoLimit] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedClientForSuggestions, setSelectedClientForSuggestions] = useState(null)
  const organizationId = useOrganizationId()
  const { canWrite, organization, isAdmin } = useOrganization()
  const { isDemo } = useSubscriptionStatus()

  // Check demo limit on mount
  useEffect(() => {
    if (isDemo) {
      initDemoSession()
      if (hasReachedDemoLimit('careplan')) {
        setReachedDemoLimit(true)
      }
    }
  }, [isDemo])

  useEffect(() => {
    if (organizationId) {
      fetchClients()
      fetchCarePlans()
      if (isAdmin) {
        fetchMembers()
      }
    }
  }, [organizationId, isAdmin])

  const fetchClients = async () => {
    if (!organizationId) return
    const { data, error } = await supabase
      .from('clients')
      .select('id, first_name, last_name')
      .eq('organization_id', organizationId)
      .order('first_name')
    if (!error && data) setClients(data)
  }

  const fetchCarePlans = async () => {
    if (!organizationId) return
    setLoading(true)
    try {
      // For demo organizations, use demo data
      if (isDemo && isDemoOrganization(organization)) {
        const demoPlans = getDemoCarePlans()
        // Merge with any localStorage entries
        const localEntries = getDemoEntries('careplan')
        const allPlans = [...demoPlans, ...localEntries]
        setPlans(allPlans)
        setLoading(false)
        return
      }

      const { data, error } = await supabase
        .from('care_plans')
        .select('*, clients(first_name, last_name)')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
      
      // Note: Archived records are included by default. Filter can be added later if needed.

      if (error) throw error
      setPlans(data || [])
    } catch (error) {
      console.error('Error fetching care plans:', error)
      alert(t('carePlans.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const fetchMembers = async () => {
    if (!organizationId) return
    try {
      const data = await orgService.getOrganizationMembers(organizationId)
      setMembers(data || [])
    } catch (error) {
      console.error('Error fetching members:', error)
    }
  }

  const filteredPlans = useMemo(() => {
    if (!searchTerm) return plans
    const term = searchTerm.toLowerCase()
    return plans.filter(
      (plan) =>
        plan.title?.toLowerCase().includes(term) ||
        plan.summary?.toLowerCase().includes(term) ||
        plan.goals?.toLowerCase().includes(term) ||
        `${plan.clients?.first_name} ${plan.clients?.last_name}`.toLowerCase().includes(term) ||
        plan.status?.toLowerCase().includes(term)
    )
  }, [plans, searchTerm])

  const paginatedPlans = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredPlans.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredPlans, currentPage])

  const totalPages = Math.ceil(filteredPlans.length / ITEMS_PER_PAGE)

  const handleDelete = async (ids) => {
    if (!canWrite('care_plans')) {
      alert(t('carePlans.permissionDenied'))
      return
    }
    try {
      const { error } = await supabase
        .from('care_plans')
        .delete()
        .in('id', ids)
        .eq('organization_id', organizationId)

      if (error) throw error
      setSelected([])
      fetchCarePlans()
    } catch (error) {
      console.error('Error deleting care plans:', error)
      alert(t('carePlans.deleteError'))
    }
  }

  const handleExport = (ids = null) => {
    exportCarePlansToPDF(plans, ids)
  }

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]))
  }

  const toggleSelectAll = () => {
    if (selected.length === paginatedPlans.length) {
      setSelected([])
    } else {
      setSelected(paginatedPlans.map((p) => p.id))
    }
  }

  const openCreateForm = () => {
    // Check demo limit before opening form
    if (isDemo && hasReachedDemoLimit('careplan')) {
      setReachedDemoLimit(true)
      return
    }
    
    setEditingPlan(null)
    formMethods.reset({
      clientId: '',
      title: '',
      summary: '',
      goals: '',
      startDate: '',
      endDate: '',
      status: 'active',
      assignedUserId: null,
    })
    setFormOpen(true)
  }

  const openEditForm = (plan) => {
    setEditingPlan(plan)
    formMethods.reset({
      clientId: plan.client_id,
      title: plan.title,
      summary: plan.summary || '',
      goals: plan.goals || '',
      startDate: plan.start_date || '',
      endDate: plan.end_date || '',
      status: plan.status || 'active',
      assignedUserId: plan.assigned_user_id || null,
    })
    setFormOpen(true)
  }

  const closeForm = () => {
    setFormOpen(false)
    setEditingPlan(null)
  }

  const formMethods = useForm({
    resolver: zodResolver(planSchema),
    defaultValues: {
      clientId: '',
      title: '',
      summary: '',
      goals: '',
      startDate: '',
      endDate: '',
      status: 'active',
    },
  })

  // Form data persistence
  const { dataRestored, clearSavedData } = useFormPersistence(
    formMethods,
    'careplans_form_data',
    null,
    [] // No fields to exclude
  )

  const onSubmit = async (values) => {
    if (!organizationId) return
    try {
      // Check demo limit
      if (isDemo && !editingPlan && hasReachedDemoLimit('careplan')) {
        setReachedDemoLimit(true)
        setFormOpen(false)
        return
      }

      // For demo users, save to localStorage instead of database
      if (isDemo) {
        const demoEntry = {
          client_id: values.clientId,
          title: values.title,
          summary: values.summary,
          goals: values.goals || null,
          start_date: values.startDate,
          end_date: values.endDate || null,
          status: values.status,
        }
        
        if (editingPlan && editingPlan.id?.startsWith('demo-')) {
          // Update localStorage entry (would need to implement update logic)
          // For now, just save as new entry
          saveDemoEntry('careplan', demoEntry)
        } else {
          saveDemoEntry('careplan', demoEntry)
        }
        
        clearSavedData()
        formMethods.reset()
        closeForm()
        fetchCarePlans()
        return
      }

      const payload = {
        organization_id: organizationId,
        client_id: values.clientId || null,
        title: values.title,
        summary: values.summary,
        goals: values.goals,
        start_date: values.startDate,
        end_date: values.endDate || null,
        status: values.status,
        assigned_user_id: values.assignedUserId || null,
      }

      if (editingPlan) {
        const { error } = await supabase
          .from('care_plans')
          .update(payload)
          .eq('id', editingPlan.id)
          .eq('organization_id', organizationId)
        if (error) throw error
      } else {
        const { error } = await supabase.from('care_plans').insert(payload)
        if (error) throw error
      }

      // Clear saved form data on success
      clearSavedData()
      formMethods.reset()
      closeForm()
      fetchCarePlans()
    } catch (error) {
      console.error('Error saving care plan:', error)
      alert(error.message || t('carePlans.saveError'))
    }
  }

  const clientOptions = useMemo(
    () =>
      clients.map((client) => ({
        value: client.id,
        label: `${client.first_name} ${client.last_name}`,
      })),
    [clients]
  )

  const memberOptions = useMemo(
    () => [
      { value: '', label: t('carePlans.generalAssignment') || 'General (No specific assignment)' },
      ...members.map((member) => ({
        value: member.user_id,
        label: member.user?.name || member.user?.email || 'Unknown',
      })),
    ],
    [members, t]
  )

  return (
    <div className="section-container">
      {/* Upgrade Prompt for Demo Users - Show proactively */}
      {isDemo && (
        <DemoUpgradePrompt featureName="care plans" limitReached={reachedDemoLimit} />
      )}

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-secondary-500 to-secondary-600 flex items-center justify-center shadow-lg">
              <Heart size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{t('carePlans.title')}</h1>
              <p className="text-gray-600">
                {t('carePlans.subtitle', { count: plans.length })}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {plans.length > 0 && (
              <Button variant="secondary" onClick={() => handleExport()} className="flex items-center gap-2 text-sm sm:text-base px-3 sm:px-6 py-2 sm:py-3">
                <Download size={16} className="sm:w-[18px] sm:h-[18px]" />
                <span className="hidden sm:inline">{t('carePlans.exportAll')}</span>
                <span className="sm:hidden">{t('common.buttons.export')}</span>
              </Button>
            )}
            <Button onClick={openCreateForm} className="flex items-center gap-2 whitespace-nowrap text-sm sm:text-base px-3 sm:px-6 py-2 sm:py-3">
              <Plus size={16} className="sm:w-[20px] sm:h-[20px]" />
              <span className="hidden sm:inline">{t('carePlans.newCarePlan')}</span>
              <span className="sm:hidden">{t('carePlans.new')}</span>
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6"
      >
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t('carePlans.searchPlaceholder')}
        />
      </motion.div>

      {/* Bulk Actions */}
      <BulkActions
        selected={selected}
        onDelete={handleDelete}
        onExport={handleExport}
        onClear={() => setSelected([])}
        resourceName="care plans"
      />

      {loading ? (
        <SkeletonContainer count={6}>
          <SkeletonListItem />
        </SkeletonContainer>
      ) : filteredPlans.length === 0 ? (
        <Card variant="soft" className="p-10 text-center">
          <Heart size={48} className="mx-auto mb-4 text-secondary-400" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {searchTerm ? t('carePlans.noMatching') : t('carePlans.noPlans')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {searchTerm
              ? t('carePlans.adjustSearch')
              : t('carePlans.getStarted')}
          </p>
          {!searchTerm && (
            <Button onClick={openCreateForm} className="flex items-center gap-2 whitespace-nowrap">
              <Plus size={20} />
              {t('carePlans.createFirst')}
            </Button>
          )}
        </Card>
      ) : (
        <>
          {paginatedPlans.length > 0 && (
            <div className="mb-4 flex items-center gap-2">
              <input
                type="checkbox"
                checked={selected.length === paginatedPlans.length && paginatedPlans.length > 0}
                onChange={toggleSelectAll}
                className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-600">{t('carePlans.selectAllPage')}</span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedPlans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card
                  variant="soft"
                  hover
                  className="p-6 h-full flex flex-col relative cursor-pointer"
                  onClick={() => navigate(`/care-plans/${plan.id}`)}
                >
                  {canWrite('care_plans') && (
                    <input
                      type="checkbox"
                      checked={selected.includes(plan.id)}
                      onChange={() => toggleSelect(plan.id)}
                      className="absolute top-4 right-4 w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 z-10"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary-400 to-secondary-500 flex items-center justify-center text-white shadow-md">
                      <Heart size={20} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className={`text-lg font-semibold ${plan.archived_at ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`}>
                          {plan.title}
                        </h3>
                        {plan.archived_at && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                            {t('carePlans.archived') || 'Archived'}
                          </span>
                        )}
                        {!plan.client_id && !plan.archived_at && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                            {t('carePlans.general') || 'General'}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {plan.clients
                          ? `${plan.clients.first_name} ${plan.clients.last_name}`
                          : plan.archived_at
                          ? t('carePlans.clientArchived') || 'Client archived'
                          : t('carePlans.generalCarePlan') || 'General Care Plan'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/care-plans/${plan.id}`)
                        }}
                        className="text-primary-600 hover:text-primary-700"
                        title={t('carePlans.viewDetails')}
                      >
                        <Eye size={18} />
                      </button>
                      {canWrite('care_plans') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            openEditForm(plan)
                          }}
                          className="text-secondary-600 hover:text-secondary-700"
                        >
                          <Pencil size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                  {plan.summary && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-3">{plan.summary}</p>
                  )}
                  {plan.goals && (
                    <div className="p-3 rounded-xl bg-secondary-50 text-secondary-700 text-sm mb-3">
                      <p className="font-semibold text-secondary-800 mb-1 flex items-center gap-2">
                        <FileText size={16} /> {t('carePlans.goals')}
                      </p>
                      <p className="line-clamp-3">{plan.goals}</p>
                    </div>
                  )}
                  <div className="mt-auto pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Calendar size={14} />
                        {plan.start_date ? formatDate(plan.start_date) : t('carePlans.noStartDate')}
                      </span>
                      <span className="uppercase tracking-wide font-semibold text-secondary-600 text-xs">
                        {plan.status || 'active'}
                      </span>
                    </div>
                    {plan.assigned_user_id && (
                      <div className="text-xs text-accent-600 mb-2">
                        Assigned: {(() => {
                          const assignedMember = members.find(m => m.user_id === plan.assigned_user_id)
                          return assignedMember?.user?.name || assignedMember?.user?.email || 'Unknown'
                        })()}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/care-plans/${plan.id}`)
                        }}
                        className="flex-1"
                      >
                        <Eye size={14} className="mr-1" />
                        {t('carePlans.view')}
                      </Button>
                      {canWrite('care_plans') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (confirm(t('carePlans.deleteConfirm'))) {
                              handleDelete([plan.id])
                            }
                          }}
                          className="px-3 py-2 text-red-600 font-semibold hover:bg-red-50 rounded-lg transition-colors text-sm flex items-center justify-center gap-1"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={ITEMS_PER_PAGE}
            totalItems={filteredPlans.length}
          />
        </>
      )}

      {/* Form Drawer */}
      <AnimatePresence>
        {formOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeForm}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-lg z-50 p-6 overflow-y-auto glass-card"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-sm uppercase text-gray-400 font-semibold tracking-wide">
                    {editingPlan ? 'Update' : 'Create'}
                  </p>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {editingPlan ? t('carePlans.editCarePlan') : t('carePlans.newCarePlan')}
                  </h3>
                </div>
                <button
                  onClick={closeForm}
                  className="p-2 rounded-full bg-gray-100 text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Template Selector & Suggestions */}
              <div className="mb-6 space-y-3">
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowTemplates(!showTemplates)
                      setShowSuggestions(false)
                    }}
                    className="flex items-center gap-1"
                  >
                    <FileText size={16} />
                    {showTemplates ? 'Hide Templates' : 'Use Template'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const clientId = formMethods.getValues('clientId')
                      if (clientId) {
                        setSelectedClientForSuggestions(clientId)
                        setShowSuggestions(!showSuggestions)
                        setShowTemplates(false)
                      } else {
                        alert('Please select a client first to get suggestions')
                      }
                    }}
                    className="flex items-center gap-1"
                  >
                    <Sparkles size={16} />
                    {showSuggestions ? 'Hide Suggestions' : 'Get Suggestions'}
                  </Button>
                </div>

                {showTemplates && (
                  <Card variant="soft" className="p-4">
                    <CarePlanTemplateSelector
                      onSelectTemplate={(template) => {
                        if (template.template_data) {
                          formMethods.reset({
                            ...formMethods.getValues(),
                            title: template.template_data.title || '',
                            summary: template.template_data.summary || '',
                            goals: template.template_data.goals || '',
                            status: template.template_data.status || 'active',
                          })
                          setShowTemplates(false)
                        }
                      }}
                      currentPlanData={formMethods.getValues()}
                    />
                  </Card>
                )}

                {showSuggestions && selectedClientForSuggestions && (
                  <Card variant="soft" className="p-4">
                    <CarePlanSuggestions
                      clientId={selectedClientForSuggestions}
                      onApplySuggestion={(suggestion) => {
                        formMethods.reset({
                          ...formMethods.getValues(),
                          title: suggestion.title,
                          summary: suggestion.description,
                          goals: suggestion.rationale,
                        })
                        setShowSuggestions(false)
                      }}
                    />
                  </Card>
                )}
              </div>

              <FormProvider {...formMethods}>
                {/* Data Restored Indicator */}
                {dataRestored && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-800 dark:text-green-200"
                  >
                    ✓ Your form data has been restored
                  </motion.div>
                )}

                <form className="space-y-4" onSubmit={formMethods.handleSubmit(onSubmit)}>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      {t('carePlans.client') || 'Client'} <span className="text-gray-500 text-xs">({t('carePlans.optional') || 'Optional'})</span>
                    </label>
                    <Select
                      options={[
                        { label: t('carePlans.generalCarePlan') || 'General Care Plan (No Client)', value: '' },
                        ...clientOptions
                      ]}
                      error={formMethods.formState.errors.clientId?.message}
                      {...formMethods.register('clientId', {
                        onChange: (e) => {
                          const value = e.target.value || null
                          formMethods.setValue('clientId', value)
                          setSelectedClientForSuggestions(value)
                          if (showSuggestions) {
                            setShowSuggestions(false)
                          }
                        },
                      })}
                    />
                    {!formMethods.watch('clientId') && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {t('carePlans.generalCarePlanDescription') || 'This care plan will be available for all clients'}
                      </p>
                    )}
                  </div>
                  
                  {/* Show tagged habits when client is selected */}
                  {formMethods.watch('clientId') && organizationId && (
                    <TaggedHabitsView
                      clientId={formMethods.watch('clientId')}
                      organizationId={organizationId}
                    />
                  )}
                  <Input
                    label="Plan Title"
                    placeholder="e.g. Hormone Balance & Detox"
                    required
                    error={formMethods.formState.errors.title?.message}
                    {...formMethods.register('title')}
                  />
                  <Textarea
                    label={t('carePlans.summary')}
                    placeholder="High-level overview..."
                    required
                    error={formMethods.formState.errors.summary?.message}
                    {...formMethods.register('summary')}
                  />
                  <Textarea
                    label={t('carePlans.goalsLabel')}
                    placeholder={t('carePlans.goalsPlaceholder')}
                    error={formMethods.formState.errors.goals?.message}
                    {...formMethods.register('goals')}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      type="date"
                      label={t('carePlans.startDate')}
                      required
                      error={formMethods.formState.errors.startDate?.message}
                      {...formMethods.register('startDate')}
                    />
                    <Input
                      type="date"
                      label={t('carePlans.endDate')}
                      error={formMethods.formState.errors.endDate?.message}
                      {...formMethods.register('endDate')}
                    />
                  </div>
                  <Select
                    label={t('carePlans.status')}
                    options={statusOptions(t)}
                    required
                    error={formMethods.formState.errors.status?.message}
                    {...formMethods.register('status')}
                  />
                  {isAdmin && (
                    <Select
                      label={t('carePlans.assignedTo') || 'Assigned To (Optional)'}
                      options={memberOptions}
                      placeholder={t('carePlans.generalAssignment') || 'General (No specific assignment)'}
                      error={formMethods.formState.errors.assignedUserId?.message}
                      {...formMethods.register('assignedUserId')}
                    />
                  )}
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1">
                      {editingPlan ? 'Update Plan' : 'Create Plan'}
                    </Button>
                    <Button type="button" variant="secondary" className="flex-1" onClick={closeForm}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </FormProvider>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default CarePlans
